/**
 * Helpers for keeping the URL's entity segment in sync with the active V2
 * division.
 *
 * Why: Beacon (and Omni) URLs are shaped `/client/:beaconClientId/...`. The
 * `:beaconClientId` is per-division — switching to another division means a
 * different default vendor (e.g. `/client/915` vs King Arthur `/client/398`).
 * After a division switch the in-app dropdown previously did
 * `window.location.reload()`, which kept the stale id in the URL. These
 * helpers compute the new URL so the caller can navigate instead of reloading.
 *
 * Only `/client/:id` is rewritten. Atlas's `/company/${encodeHash(0)}` is
 * already a self-relative reference (the cookie controls which company is
 * shown), so `/company/` paths are intentionally left alone.
 */

const CLIENT_PATH_PATTERN = /^\/client\/[^/]+(.*)$/;

/**
 * Replace the `:id` in a `/client/:id...` path with `newClientId`, preserving
 * any tail segments. Returns `null` when the pathname is not a `/client/:id`
 * path (so callers know to fall back to a plain reload).
 */
export const rewriteClientPath = (
  pathname: string,
  newClientId: number | string
): string | null => {
  const match = pathname.match(CLIENT_PATH_PATTERN);
  if (!match) return null;
  const tail = match[1];
  return `/client/${newClientId}${tail}`;
};

/**
 * Build the post-switch URL by rewriting the current `window.location` against
 * the new division's default Beacon client id. Preserves search and hash.
 * Returns `null` when no rewrite is needed (caller should reload() instead).
 */
export const buildPostSwitchUrl = (
  newClientId: number | string
): string | null => {
  const newPath = rewriteClientPath(window.location.pathname, newClientId);
  if (newPath === null) return null;
  return `${newPath}${window.location.search}${window.location.hash}`;
};
